import axios from 'axios'
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  setAccessToken
} from '@/utils/auth-storage'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''
let refreshPromise = null
const INVALID_ACCESS_TOKEN_CODE = 12103

function normalizeApiUrl(base, url) {
  if (!url) return url
  if (/^https?:\/\//i.test(url)) return url

  const normalizedBase = (base || '').replace(/\/+$/, '')
  const normalizedUrl = String(url).startsWith('/') ? String(url) : `/${String(url)}`

  // baseURL already contains '/api' and request url also starts with '/api'
  // => prevent '/api/api/...'
  if (normalizedBase.endsWith('/api') && normalizedUrl.startsWith('/api/')) {
    return normalizedUrl.replace(/^\/api/, '')
  }

  return normalizedUrl
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise

  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  refreshPromise = axios.post(
    `${baseURL || ''}/v1/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  )
    .then((response) => {
      const nextToken = response?.data?.data?.accessToken
      if (!nextToken || response?.data?.code !== 0) {
        return null
      }
      setAccessToken(nextToken)
      return nextToken
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

function isAuthEndpointUrl(url) {
  const requestUrl = String(url || '')
  return requestUrl.includes('/v1/auth/sign-in')
    || requestUrl.includes('/v1/auth/sns/')
    || requestUrl.includes('/v1/auth/refresh')
    || requestUrl.includes('/v1/auth/logout')
}

function redirectToLogin() {
  clearAuthStorage()
  if (typeof window === 'undefined') return

  const currentPath = `${window.location.pathname || ''}${window.location.search || ''}`
  const redirect = currentPath && currentPath !== '/login'
    ? `?redirect=${encodeURIComponent(currentPath)}`
    : ''
  window.location.replace(`/login${redirect}`)
}

async function refreshAndRetry(originalRequest) {
  originalRequest._retry = true
  const nextToken = await refreshAccessToken()
  if (nextToken) {
    originalRequest.headers = {
      ...originalRequest.headers,
      Authorization: `Bearer ${nextToken}`
    }
    return api(originalRequest)
  }
  redirectToLogin()
  return Promise.reject(new Error('session expired'))
}

const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

api.interceptors.request.use((config) => {
  config.url = normalizeApiUrl(config.baseURL || baseURL, config.url)
  if (!config.headers?.Authorization) {
    const token = getAccessToken()
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`
      }
    }
  }
  return config
})

api.interceptors.response.use(
  async (response) => {
    const originalRequest = response?.config
    const code = response?.data?.code

    if (
      originalRequest
      && !originalRequest._retry
      && !isAuthEndpointUrl(originalRequest.url)
      && code === INVALID_ACCESS_TOKEN_CODE
    ) {
      return refreshAndRetry(originalRequest)
    }

    return response
  },
  async (error) => {
    const originalRequest = error?.config
    const status = error?.response?.status
    const hadAuthHeader = Boolean(originalRequest?.headers?.Authorization)
    const isAuthEndpoint = isAuthEndpointUrl(originalRequest?.url)

    if (!originalRequest || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error)
    }

    // Retry only when it looks like an auth-token failure.
    const shouldRefresh = status === 401 || (status === 403 && hadAuthHeader)
    if (shouldRefresh) {
      return refreshAndRetry(originalRequest)
    }

    return Promise.reject(error)
  }
)

export default api
